/**
 * localStorage.ts
 * 封装 localStorage 支持 expiration + SM2 加解密
 */

import sm2Util from './sm2Util'

interface Options {
  expire?: number
  encrypt?: boolean
}

interface Stored {
  data: string
  expire: number
}

/** 包装写入值为 JSON 字符串 + 过期时间戳 */
function wrap(value: string, expire: number): string {
  const obj: Stored = { data: value, expire }
  return JSON.stringify(obj)
}

/** 解析存储结构 */
function unwrap(raw: string | null): Stored | null {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const storage = window.localStorage

export default {
  set(key: string, value: any, options: Options = {}): boolean {
    const { expire = 0, encrypt = false } = options
    let str = typeof value === 'string' ? value : JSON.stringify(value)

    if (encrypt) {
      const enc = sm2Util.encrypt(str)
      if (!enc) return false
      str = enc
    }

    const expireAt = expire > 0 ? Date.now() + expire : 0
    storage.setItem(key, wrap(str, expireAt))
    return true
  },

  get(key: string, options: Options = {}): any {
    const { encrypt = false } = options
    const raw = storage.getItem(key)
    const obj = unwrap(raw)
    if (!obj) return null

    if (obj.expire && obj.expire < Date.now()) {
      storage.removeItem(key)
      return null
    }

    let str = obj.data
    if (encrypt) {
      const dec = sm2Util.decrypt(str)
      if (dec === null) return null
      str = dec
    }

    try {
      return JSON.parse(str)
    } catch {
      return str
    }
  },

  remove(key: string) {
    storage.removeItem(key)
  },

  clear() {
    storage.clear()
  }
}
